import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * How the photo was captured. Useful for analytics ("how often does
 * auto-capture fire vs scan-triggered?"), and for downstream consumers
 * that want to weight evidence (auto-captured shots are unattended,
 * scan-triggered shots are explicitly framed by the operator).
 */
export enum ParcelPhotoSource {
  /** Operator scanned the configured capture control barcode. */
  SCAN = "scan",
  /** Operator clicked the capture button in the UI or hit the hotkey. */
  MANUAL = "manual",
  /** Motion-detection auto-capture fired (one-shot per FG, opt-in). */
  AUTO = "auto",
  /**
   * Operator's device had no live camera permission/availability — fell
   * back to the OS file picker / native camera capture.
   */
  FILE = "file",
}

/**
 * One captured photo of a packed parcel. Stored embedded on the
 * `FulfillmentGroup.parcelPhotos` array.
 *
 * Identity & access:
 *   - `_id` is the only identifier ever exposed in the merchant app's
 *     URL bar. Mongo `ObjectId` so it's opaque and unguessable.
 *   - `url` is the resolved location for fetching the image bytes. The
 *     server generates it; it must carry no team/FG/timestamp/order
 *     info in the path. A typical value is an authenticated proxy
 *     endpoint (`/api/parcel-photos/<_id>`) or a CDN URL whose path is
 *     a UUID with nothing else. The bucket / S3 key underneath is
 *     server-side detail and is not stored on the document.
 *   - The team / FG / operator association lives ONLY in this record.
 *
 * Lifecycle:
 *   - `confirmedAt` flips when the client posts a `finalize` confirming
 *     the underlying object exists. Unconfirmed rows are swept after a
 *     short TTL by a background job — covers the "upload PUT failed
 *     silently" edge case.
 *   - Soft-deleting sets `deletedAt`; a sweeper later removes the
 *     underlying object so admin tooling can undo accidental deletes
 *     for a short window.
 */
const ParcelPhotoSchema_ = z.object({
  _id: zExt.objectId(),
  /**
   * Server-generated, opaque URL for fetching the photo. Either an
   * authenticated proxy path (`/api/parcel-photos/<_id>`) or a CDN URL
   * with no identifying info in the path. Storage-layer details
   * (bucket, key, signing) are not encoded here — the server owns the
   * mapping from this URL to underlying bytes and may re-issue the URL
   * during a storage migration without touching call sites.
   */
  url: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  sizeBytes: z.number().int().nonnegative(),
  /** MIME type — `image/jpeg` for compressed captures, `image/png` for raw. */
  contentType: z.string(),
  /** When the operator captured the frame (client-side wall clock at capture). */
  capturedAt: z.coerce.date(),
  /** When the server marked the row confirmed (underlying object verified to exist). */
  confirmedAt: z.coerce.date().nullish(),
  capturedByUserId: zExt.objectId().nullish(),
  source: createEnum(ParcelPhotoSource, { enumName: "ParcelPhotoSource" }),
  /** Soft-delete marker. Sweeper removes the underlying object on a delay. */
  deletedAt: z.coerce.date().nullish(),
});
export const ParcelPhotoSchema: z.ZodType<ParcelPhoto> = ParcelPhotoSchema_;
export interface ParcelPhoto extends z.infer<typeof ParcelPhotoSchema_> {}
export interface IParcelPhotoSchema extends z.ZodType<ParcelPhoto> {}

/**
 * Hard-coded control barcodes the scan-to-verify page intercepts when
 * the parcel-photo feature is enabled. Not configurable per team — the
 * UI prints a card with these exact strings and operators scan it.
 */
export const PARCEL_PHOTO_CAPTURE_BARCODE = "*PARCEL-PHOTO*";
export const PARCEL_PHOTO_RETAKE_BARCODE = "*PARCEL-PHOTO-RETAKE*";
export const PARCEL_PHOTO_CANCEL_BARCODE = "*PARCEL-PHOTO-CANCEL*";

// (Team-scoped verify-parcel-photo settings are defined as Mongoose
// schemas in `redo/backend/db/schema/src/team/OutboundLabelSettings.ts`
// and surfaced as plain TS types via `redo/model/src/team.ts`. We used
// to keep a Zod mirror here for would-be RPC validation, but nothing
// consumed it and the nested `.default(...)` shape kept tripping
// TS2769 against Zod's overloaded `.default` signatures. Left out
// rather than fight the typing — re-add a Zod schema if/when an RPC
// boundary actually needs one.)
